import axiosInstance from '../AxiosClient'
import {
  donationShape,
  donationCreateShape,
  donationFiltersShape,
  IRCSpringBoardDonationInputShape,
  WWFEngaingNetworkDonationInputShape,
  donationFilterValuesShape,
  canvassingOrgAdminChartInputShape,
  exportDonationTemplateShape,
  storeDonationTemplateShape,
  removeDonationTemplateShape,
  donorSearchInputShape,
  donorSearchOutputShape,
  fundraiserSearchInputShape,
  fundraiserSearchOutputShape,
  smsDonorForDonationVerificationInput,
  updateDonationVerificationStatusInput,
  updateDonationNotesInput,
  donationDuplicatesInput,
  donationDuplicatesOutput,
  givarooIdSearchInputShape, // Add this line
  givarooIdSearchOutputShape,
} from './models'
import {
  createApi,
  createCustomServiceCall,
  createCollectionManager,
  Pagination,
} from '@thinknimble/tn-models'
import fileDownload from 'js-file-download'
import { z } from 'zod'
import { appendFormToken, formTokenQuery } from '../formAccess'

const DONATIONS_ENDPOINT = '/donations/'
const FILTER_TEMPLATES_ENDPOINT = '/filterTemplates/'
const DONATION_VERIFICAION_STATUS = '/donationVerificationStatus/'

const customCreate = createCustomServiceCall(
  {
    inputShape: donationCreateShape,
    outputShape: donationShape,
  },
  async ({ client, utils, input }) => {
    const { fundraiser, formToken, ...data } = input
    let formattedData = utils.toApi(data)
    const params = formTokenQuery(formToken)
    const res = await client.post(`${DONATIONS_ENDPOINT}${params}`, formattedData)
    return utils.fromApi(res.data)
  },
)

const createSpringBoardDonation = createCustomServiceCall(
  {
    inputShape: IRCSpringBoardDonationInputShape,
  },
  async ({ client, utils, input }) => {
    const { formToken, ...data } = input
    let formattedData = utils.toApi(data)
    const params = appendFormToken('?SpringBoard=true', formToken)
    const res = await client.post(`${DONATIONS_ENDPOINT}${params}`, formattedData)
    // Return full response — donation_status / no_response are required for pending & QR flows.
    return res.data
  },
)

const createEngaingNetworkDonation = createCustomServiceCall(
  {
    inputShape: WWFEngaingNetworkDonationInputShape,
  },
  async ({ client, utils, input }) => {
    const { formToken, ...data } = input
    let formattedData = utils.toApi(data)
    const params = appendFormToken('?EngagingNetwork=true', formToken)
    const res = await client.post(`${DONATIONS_ENDPOINT}${params}`, formattedData)
    return res.data
  },
)

const createNAACPDonation = createCustomServiceCall(
  {
    inputShape: IRCSpringBoardDonationInputShape,
  },
  async ({ client, utils, input }) => {
    const { formToken, ...data } = input
    let formattedData = utils.toApi(data)
    const params = appendFormToken('?NAACPCustomAPI=true', formToken)
    const res = await client.post(`${DONATIONS_ENDPOINT}${params}`, formattedData)
    return res.data
  },
)

// Server-side proxy for Luminate Online's CRDonationAPI `donateCheckout` call.
// Luminate refuses cross-origin requests from the browser, so we hand the
// donateCheckout parameters to Django and let it relay them server-to-server.
// Body is passed through unchanged (Luminate expects dotted field names like
// `donor.email` and `billing.name.first` as-is).
//
// NOTE: tn-models requires a non-void `inputShape` for `input` to reach the
// callback at all. With `inputShape: undefined` it defaults to `z.void()`
// and silently drops everything we pass in. A permissive `z.any()` field is
// enough to flip `expectsInput` to true; the library does NOT auto-validate
// or transform the payload (only `utils.toApi(input)` does that, and we
// don't call it here because Luminate's dotted keys aren't snake-case).
const resubmitDonation = createCustomServiceCall(
  {
    inputShape: { donationId: z.string().uuid() },
  },
  async ({ client, input }) => {
    const res = await client.post(`${DONATIONS_ENDPOINT}${input.donationId}/resubmit/`)
    const data = res.data
    return {
      donationId: data.donation_id,
      donationStatus: data.donation_status,
      message: data.message,
      transactionErrorMsg: data.transaction_error_msg,
      canResubmit: data.can_resubmit,
      submissionId: data.submission_id,
      transactionId: data.transaction_id,
    }
  },
)

const getResubmitData = createCustomServiceCall(
  {
    inputShape: { donationId: z.string().uuid() },
  },
  async ({ client, input }) => {
    const res = await client.get(`${DONATIONS_ENDPOINT}${input.donationId}/resubmit_data/`)
    const data = res.data
    // Keep submission payload keys as stored (snake_case). fromApi on the whole
    // response would camelCase nested fields and break resubmitPrefill mapping.
    return {
      donationId: data.donation_id,
      campaignId: data.campaign_id,
      fundraiserId: data.fundraiser_id,
      provider: data.provider,
      formPrefill: data.form_prefill,
    }
  },
)

const updateDonationNotes = createCustomServiceCall(
  {
    inputShape: updateDonationNotesInput,
  },
  async ({ client, input }) => {
    const res = await client.patch(`${DONATIONS_ENDPOINT}${input.donationId}/notes/`, {
      notes: input.notes,
    })
    return {
      id: res.data.id,
      notes: res.data.notes,
    }
  },
)

const getDonationDuplicates = createCustomServiceCall(
  {
    inputShape: donationDuplicatesInput,
    outputShape: donationDuplicatesOutput,
  },
  async ({ client, utils, input }) => {
    const res = await client.get(`${DONATIONS_ENDPOINT}${input.donationId}/duplicates/`)
    return utils.fromApi(res.data)
  },
)

// Blackbaud SKY Payments: Django authorizes the card with Blackbaud, then records the
// gift in Luminate. The body is already snake_case and carries nested objects
// (custom_questions keyed by Luminate element names), so it is posted as-is rather than
// run through `utils.toApi`, which would rewrite those keys.
const createBBMSSkyDonation = createCustomServiceCall(
  {
    inputShape: { payload: z.any() },
  },
  async ({ client, input }) => {
    const { formToken, ...body } = input || {}
    const params = appendFormToken('?BBMSSkyPayments=true', formToken)
    const res = await client.post(`${DONATIONS_ENDPOINT}${params}`, body)
    return res.data
  },
)

const submitBBMSCheckout = createCustomServiceCall(
  {
    inputShape: { payload: z.any() },
  },
  async ({ client, input }) => {
    const params = formTokenQuery(input?.formToken)
    const { fundraiser: _f, formToken: _t, ...body } = input || {}
    const res = await client.post(`${DONATIONS_ENDPOINT}submit_bbms_checkout/${params}`, body)
    return res.data
  },
)

const generateExport = createCustomServiceCall(
  {
    inputShape: donationFiltersShape,
  },
  async ({ client, utils, input }) => {
    const { ...data } = input
    let formattedData = utils.toApi(data)

    client
      .post(`${DONATIONS_ENDPOINT}generate_donations_export/`, formattedData, {
        responseType: 'blob', // important
      })
      .then((response) => {
        fileDownload(response.data, 'donations_export.csv')
      })
  },
)

const retrieveFilterIndexes = createCustomServiceCall(
  {
    outputShape: donationFilterValuesShape,
  },
  async ({ client, utils }) => {
    const res = await client.get(`${DONATIONS_ENDPOINT}get_filter_values/`)
    return utils.fromApi(res.data)
  },
)

const retrieveMapLocations = createCustomServiceCall(
  {
    inputShape: donationFiltersShape,
  },
  async ({ client, utils, input }) => {
    const { ordering: _ordering, ...filters } = input || {}
    const formattedData = utils.toApi(filters)
    const params = new URLSearchParams()
    Object.entries(formattedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value)
      }
    })
    const query = params.toString()
    const res = await client.get(`${DONATIONS_ENDPOINT}map_locations/${query ? `?${query}` : ''}`)
    return (res.data || []).map((item) => ({
      givarooId: item.givaroo_id,
      datetimeCreated: item.datetime_created,
      campaignName: item.campaign_name,
      campaignLocation: item.campaign_location,
      fundraiserName: item.fundraiser_name,
      canvassingOrgName: item.canvassing_org_name,
      latitude: item.latitude,
      longitude: item.longitude,
      recordSource: item.record_source,
      status: item.status,
      donorAge: item.donor_age,
      currentDonorStatus: item.current_donor_status,
      currentDonorStatusValue: item.current_donor_status_value,
      paymentsBeforeCancellation: item.payments_before_cancellation,
    }))
  },
)
const donor_age_pie_chart = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)
    let params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}`
    if (
      fundraiser_filter_value &&
      fundraiser_filter_value !== 'undefined' &&
      fundraiser_filter_value.trim()
    ) {
      params += `&fundraiser_list=${fundraiser_filter_value}`
    }
    const res = await client.get(`${DONATIONS_ENDPOINT}donor_age_pie_chart/${params}`)
    return res.data
  },
)

const donation_amount_pie_chart = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)
    let params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}`
    if (
      fundraiser_filter_value &&
      fundraiser_filter_value !== 'undefined' &&
      fundraiser_filter_value.trim()
    ) {
      params += `&fundraiser_list=${fundraiser_filter_value}`
    }
    const res = await client.get(`${DONATIONS_ENDPOINT}donation_amount_pie_chart/${params}`)
    return res.data
  },
)

const number_of_donors_by_time = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}`
    const res = await client.get(`${DONATIONS_ENDPOINT}number_of_donors_by_time/${params}`)
    return res.data
  },
)

//For Fundraisers

const get_fundraiser_performance_metrics_for_admin = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}&fundraiser_list=${fundraiser_filter_value}`
    const res = await client.get(
      `${DONATIONS_ENDPOINT}get_fundraiser_performance_metrics_for_admin/${params}`,
    )
    return res.data
  },
)

const get_campaign_performance_metrics = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}`
    const res = await client.get(`${DONATIONS_ENDPOINT}get_campaign_performance_metrics/${params}`)
    return res.data
  },
)

const fundraiser_metrics = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}`
    const res = await client.get(`${DONATIONS_ENDPOINT}fundraiser_metrics/${params}`)
    return res.data
  },
)

const donor_analytics_pie_charts_for_fundraisers = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}`
    const res = await client.get(
      `${DONATIONS_ENDPOINT}donor_analytics_pie_charts_for_fundraisers/${params}`,
    )
    return res.data
  },
)

const leaderboard = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    // Get type, period, and date directly from input
    const type = input.type || 'individual'
    const period = input.period || 'daily'
    const date = input.date || input.start_date || new Date().toISOString().split('T')[0]
    const params = `?type=${type}&period=${period}&date=${date}`
    const res = await client.get(`${DONATIONS_ENDPOINT}leaderboard/${params}`)
    return res.data
  },
)

const leaderboardRecords = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${DONATIONS_ENDPOINT}leaderboard_records/`)
  return res.data
})

const form_type_pie_chart = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)

    // Build params, only including campaign_list if it's defined and not empty
    let params = `?start_date=${start_date}&end_date=${end_date}`

    if (
      campaign_filter_value &&
      campaign_filter_value !== 'undefined' &&
      campaign_filter_value.trim()
    ) {
      params += `&campaign_list=${campaign_filter_value}`
    }

    if (
      fundraiser_filter_value &&
      fundraiser_filter_value !== 'undefined' &&
      fundraiser_filter_value.trim()
    ) {
      params += `&fundraiser_list=${fundraiser_filter_value}`
    }

    const res = await client.get(`${DONATIONS_ENDPOINT}form_type_pie_chart/${params}`)
    return res.data
  },
)

const getM1Percentage = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)

    let params = `?start_date=${start_date}&end_date=${end_date}`

    if (
      campaign_filter_value &&
      campaign_filter_value !== 'undefined' &&
      campaign_filter_value.trim()
    ) {
      params += `&campaign_list=${campaign_filter_value}`
    }

    if (
      fundraiser_filter_value &&
      fundraiser_filter_value !== 'undefined' &&
      fundraiser_filter_value.trim()
    ) {
      params += `&fundraiser_list=${fundraiser_filter_value}`
    }

    const res = await client.get(`${DONATIONS_ENDPOINT}m1_percentage/${params}`)
    return res.data
  },
)

const exportCustomizeTemplate = createCustomServiceCall(
  {
    inputShape: exportDonationTemplateShape,
  },
  async ({ client, input, utils }) => {
    try {
      const { ...data } = input
      let formattedData = utils.toApi(data)
      const response = await client.post(
        `${DONATIONS_ENDPOINT}download_custom_template/`,
        formattedData,
        {
          responseType: 'blob',
        },
      )
      fileDownload(response.data, 'donations_export.csv')
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const saveExportTemplate = createCustomServiceCall(
  {
    inputShape: storeDonationTemplateShape,
  },
  async ({ client, input, utils }) => {
    const { ...data } = input
    let formattedData = utils.toApi(data)
    const response = await client.post(`${FILTER_TEMPLATES_ENDPOINT}`, formattedData)
    return response.data
  },
)

const getExportTemplate = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${FILTER_TEMPLATES_ENDPOINT}`)
  return res.data
})

const removeSavedFilterTemplate = createCustomServiceCall(
  {
    inputShape: removeDonationTemplateShape,
  },
  async ({ client, input, utils }) => {
    const { template_id } = utils.toApi(input)
    const params = `?id=${template_id}`
    const res = await client.delete(`${FILTER_TEMPLATES_ENDPOINT}delete_template/${params}`)
    return res.data
  },
)

const searchDonors = createCustomServiceCall(
  {
    inputShape: donorSearchInputShape,
    outputShape: donorSearchOutputShape,
  },
  async ({ client, input }) => {
    const { query, fundraiser, status, frequency, campaign } = input
    let params = `query=${query}`

    if (fundraiser) params += `&fundraiser=${fundraiser}`
    if (status) params += `&status=${status}`
    if (frequency) params += `&frequency=${frequency}`
    if (campaign) params += `&campaign=${campaign}`

    const res = await client.get(`${DONATIONS_ENDPOINT}search_donors/?${params}`)
    return res.data
  },
)

const searchFundraisers = createCustomServiceCall(
  {
    inputShape: fundraiserSearchInputShape,
    outputShape: fundraiserSearchOutputShape,
  },
  async ({ client, input }) => {
    const { query } = input
    const res = await client.get(`${DONATIONS_ENDPOINT}search_fundraisers/?query=${query}`)
    return res.data
  },
)

const smsDonorForDonationVerification = createCustomServiceCall(
  {
    inputShape: smsDonorForDonationVerificationInput,
  },
  async ({ client, input }) => {
    const response = await client.post(
      `${DONATIONS_ENDPOINT}send_sms_for_donation_verification/`,
      {},
      {
        params: {
          mobile_phone: input.mobilePhone,
          url: input.url,
          unique_id: input.uniqueId,
          donor_name: input.donorName,
          ...(input.formToken ? { form_token: input.formToken } : {}),
        },
      },
    )
    return response.data
  },
)

const updateDonationVerificationStatus = createCustomServiceCall(
  {
    inputShape: updateDonationVerificationStatusInput,
  },
  async ({ client, input }) => {
    const response = await client.post(
      `${DONATION_VERIFICAION_STATUS}verify/`,
      {},
      {
        params: {
          unique_id: input.uniqueId,
          verified: input.verified,
          ...(input.formToken
            ? { form_token: input.formToken }
            : input.legacyFundraiser
            ? { fundraiser: input.legacyFundraiser }
            : {}),
        },
      },
    )
    return response.data
  },
)

const searchGivarooIds = createCustomServiceCall(
  {
    inputShape: givarooIdSearchInputShape,
    outputShape: givarooIdSearchOutputShape,
  },
  async ({ client, input }) => {
    const { query } = input
    const res = await client.get(`${DONATIONS_ENDPOINT}search_givaroo_ids/?query=${query}`)
    return res.data
  },
)

export const DonationApi = createApi(
  {
    baseUri: DONATIONS_ENDPOINT,
    client: axiosInstance,
    models: {
      entity: donationShape,
      create: donationCreateShape,
      extraFilters: donationFiltersShape,
    },
  },
  {
    customCreate,
    generateExport,
    createSpringBoardDonation,
    createEngaingNetworkDonation,
    retrieveFilterIndexes,
    retrieveMapLocations,
    donor_age_pie_chart,
    donation_amount_pie_chart,
    number_of_donors_by_time,
    get_fundraiser_performance_metrics_for_admin,
    get_campaign_performance_metrics,
    fundraiser_metrics,
    donor_analytics_pie_charts_for_fundraisers,
    leaderboard,
    leaderboardRecords,
    form_type_pie_chart,
    getM1Percentage,
    exportCustomizeTemplate,
    saveExportTemplate,
    getExportTemplate,
    removeSavedFilterTemplate,
    searchDonors,
    searchFundraisers,
    smsDonorForDonationVerification,
    updateDonationVerificationStatus,
    searchGivarooIds,
    createNAACPDonation,
    submitBBMSCheckout,
    createBBMSSkyDonation,
    getResubmitData,
    resubmitDonation,
    updateDonationNotes,
    getDonationDuplicates,
  },
)

export const donationFunctions = () => {
  const donationFilters = {
    ordering: '-givaroo_id',
    campaign: '',
    dateRangeStart: '',
    dateRangeEnd: '',
    fundraiser: '',
    frequency: '',
    status: '',
    currentDonorStatus: '',
    donor: '',
    givarooId: '',
  }

  const donationCollection = createCollectionManager({
    fetchList: DonationApi.list,
    filters: donationFilters,
    pagination: new Pagination(),
  })

  return {
    donationCollection,
    donationFilters,
  }
}
